import React from 'react'
import { Box, Plane, Text } from '@react-three/drei'
import { useDesignStore, type ADUModel } from '../../store/designStore'

interface ADUInteriorViewProps {
  adu: ADUModel
}

const ADUInteriorView: React.FC<ADUInteriorViewProps> = ({ adu }) => {
  const { aduEquipment, getADUEquipmentForADU } = useDesignStore()
  const equipment = getADUEquipmentForADU(adu.id)

  // Calculate room layout based on ADU features
  const { width, height, depth } = adu.dimensions
  const roomLayout = calculateRoomLayout(adu, width, depth)

  return (
    <group position={adu.position} rotation={adu.rotation}>
      {/* Floor Plan */}
      <Plane 
        args={[width, depth]} 
        rotation={[-Math.PI / 2, 0, 0]} 
        position={[0, 0.1, 0]}
      >
        <meshStandardMaterial color="#f5f5f5" transparent opacity={0.8} />
      </Plane>

      {/* Room Dividers/Walls */}
      {roomLayout.walls.map((wall, index) => (
        <Box
          key={`wall-${index}`}
          args={wall.dimensions}
          position={wall.position}
        >
          <meshStandardMaterial color="#e2e8f0" transparent opacity={0.6} />
        </Box>
      ))}

      {/* Room Labels */}
      {roomLayout.rooms.map((room, index) => (
        <Text
          key={`room-${index}`}
          position={[room.position[0], 0.2, room.position[2]]}
          rotation={[-Math.PI / 2, 0, 0]}
          fontSize={1}
          color="#4a5568"
          anchorX="center"
          anchorY="middle"
        >
          {room.name}
        </Text>
      ))}

      {/* Drop Zones for each room */}
      {roomLayout.rooms.map((room, index) => (
        <Plane
          key={`dropzone-${index}`}
          args={[room.width - 1, room.depth - 1]}
          position={[room.position[0], 0.05, room.position[2]]}
          rotation={[-Math.PI / 2, 0, 0]}
          userData={{ roomType: room.type, roomName: room.name }}
        >
          <meshStandardMaterial 
            color={getDropZoneColor(room.type)} 
            transparent 
            opacity={0.3}
          />
        </Plane>
      ))}

      {/* Exterior walls (transparent) */}
      <group>
        {/* Front wall */}
        <Box args={[width, height, 0.2]} position={[0, height/2, depth/2]}>
          <meshStandardMaterial color="#cbd5e0" transparent opacity={0.3} />
        </Box>
        {/* Back wall */}
        <Box args={[width, height, 0.2]} position={[0, height/2, -depth/2]}>
          <meshStandardMaterial color="#cbd5e0" transparent opacity={0.3} />
        </Box>
        {/* Left wall */}
        <Box args={[0.2, height, depth]} position={[-width/2, height/2, 0]}>
          <meshStandardMaterial color="#cbd5e0" transparent opacity={0.3} />
        </Box>
        {/* Right wall */}
        <Box args={[0.2, height, depth]} position={[width/2, height/2, 0]}>
          <meshStandardMaterial color="#cbd5e0" transparent opacity={0.3} />
        </Box>
      </group>
    </group>
  )
}

// Helper function to calculate room layout based on ADU features
function calculateRoomLayout(adu: ADUModel, width: number, depth: number) {
  const { features } = adu
  const rooms: Array<{
    name: string
    type: string
    position: [number, number, number]
    width: number
    depth: number
  }> = []
  const walls: Array<{
    dimensions: [number, number, number]
    position: [number, number, number]
  }> = []

  // Basic layout logic based on features
  let currentX = -width/2
  let currentZ = -depth/2
  
  // Living room (always present)
  const livingWidth = width * 0.4
  const livingDepth = depth * 0.6
  rooms.push({
    name: 'Living Room',
    type: 'living',
    position: [currentX + livingWidth/2, 0, currentZ + livingDepth/2],
    width: livingWidth,
    depth: livingDepth
  })

  // Kitchen (if present)
  if (features.kitchen) {
    const kitchenWidth = width * 0.3
    const kitchenDepth = depth * 0.4
    rooms.push({
      name: 'Kitchen',
      type: 'kitchen',
      position: [currentX + livingWidth + kitchenWidth/2, 0, currentZ + kitchenDepth/2],
      width: kitchenWidth,
      depth: kitchenDepth
    })
  }

  // Bedroom(s)
  if (features.bedrooms > 0) {
    const bedroomWidth = width * 0.35
    const bedroomDepth = depth * 0.4
    for (let i = 0; i < features.bedrooms; i++) {
      rooms.push({
        name: `Bedroom ${i + 1}`,
        type: 'bedroom',
        position: [currentX + bedroomWidth/2, 0, currentZ + livingDepth + bedroomDepth/2],
        width: bedroomWidth,
        depth: bedroomDepth
      })
      currentX += bedroomWidth
    }
  }

  // Bathroom(s)
  if (features.bathrooms > 0) {
    const bathroomWidth = width * 0.25
    const bathroomDepth = depth * 0.3
    for (let i = 0; i < Math.floor(features.bathrooms); i++) {
      rooms.push({
        name: `Bathroom ${i + 1}`,
        type: 'bathroom',
        position: [width/2 - bathroomWidth/2, 0, currentZ + bathroomDepth/2],
        width: bathroomWidth,
        depth: bathroomDepth
      })
    }
  }

  // Add interior walls between rooms
  rooms.forEach((room, index) => {
    if (index > 0) {
      // Add wall between rooms (simplified)
      walls.push({
        dimensions: [0.1, 8, room.depth],
        position: [room.position[0] - room.width/2, 4, room.position[2]]
      })
    }
  })

  return { rooms, walls }
}

// Helper function to get drop zone colors based on room type
function getDropZoneColor(roomType: string): string {
  const colors = {
    living: '#48bb78',      // Green for living room
    kitchen: '#ed8936',     // Orange for kitchen
    bedroom: '#4299e1',     // Blue for bedroom
    bathroom: '#9f7aea',    // Purple for bathroom
    dining: '#38b2ac',      // Teal for dining
    office: '#f56565',      // Red for office
    storage: '#a0aec0'      // Gray for storage
  }
  return colors[roomType as keyof typeof colors] || '#718096'
}

export default ADUInteriorView
